import { theme } from "@common/theme/theme"
import { useAutoBypassDeepLink } from "@data/auth/useAutoBypassDeepLink"
import { switchAccount } from "@data/redux/actions/authTokens"
import { clearPendingDeepLink } from "@data/redux/actions/notifications"
import { authService } from "@data/services/authService"
import { useNavigation } from "@react-navigation/native"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useEffect, useState } from "react"
import { StyleSheet, View } from "react-native"
import { connect } from "react-redux"
import { useApiStatus } from "../common/hooks/useApiStatus"
import { apiIdle, apiRequest } from "../data/redux/actions/api"
import {
    setIsParentMode,
    setParentModeFamilyId,
} from "../data/redux/actions/parentMode"
import { resolveAutoBypassTarget } from "../data/utility/familyUtils"

/* global require */

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: "#fff",
        alignItems: "center",
        justifyContent: "flex-start",
    },
})

/**
 * The welcome of a device that has stored accounts (`menu`, logged-out stack):
 * a blank screen on its way to « Qui utilise Masteur ? », or straight into the
 * account a pending deep link points to. A device with no account never gets
 * here — `welcomeBridge` (`featureFlags/screenBridge.js`) gives it the
 * design-system login; the old welcome carousel is gone (26/10).
 */
export const AuthNavigator = (props) => {
    const { storedTokens } = props

    const nav = useNavigation()

    const [autoBypassToken, setAutoBypassToken] = useState(null)

    // Auto-bypass family selection when a pending deep link resolves to a
    // single-account target. Ref-tracking / resolution lives in the hook;
    // this component owns the side effect (validate the stored token so
    // switchAccount can log the user in).
    useAutoBypassDeepLink(
        (target) => {
            setAutoBypassToken({
                data: target.token,
                isParentMode: target.isParentMode,
                familyId: target.familyId,
            })
            props.apiRequest(
                authService.validateToken,
                null,
                null,
                null,
                null,
                target.token.token
            )
        },
        {
            enabled:
                storedTokens?.length > 0 &&
                !props.isParentMode &&
                !props.isLiveMode,
        }
    )

    // Handle auto-bypass token validation. The error dialog only for the
    // validation this screen started: « Qui utilise Masteur ? », mounted on
    // top of it, validates tokens too and reports its own failures.
    useApiStatus(
        authService.validateToken,
        null,
        !!autoBypassToken,
        () => {
            if (!autoBypassToken) return
            const tokenData = autoBypassToken
            if (tokenData.isParentMode) {
                if (tokenData.familyId !== null) {
                    props.setParentModeFamilyId(tokenData.familyId)
                }
                props.setIsParentMode(true)
            }
            props.switchAccount(tokenData.data)
            setAutoBypassToken(null)
        },
        () => {
            if (!autoBypassToken) return
            // Auto-bypass failed — discard the pending deep link so
            // FamilyAccountSelectionScreen doesn't retry the same invalid
            // token revalidation, and fall back to the normal selection UI.
            setAutoBypassToken(null)
            props.apiIdle(authService.validateToken)
            props.clearPendingDeepLink()
            nav.replace("family-account-selection")
        }
    )

    // Auto-redirect to family account selection first if there are family accounts
    useEffect(() => {
        if (storedTokens && Object.keys(storedTokens).length > 0) {
            // Get current route name to avoid unnecessary redirects
            const currentRoute = nav.getState()?.routes?.[nav.getState()?.index]

            // The impersonation bootstrap owns its own auth handoff — it
            // overwrites the session in-flight and the bootstrapImpersonation
            // reducer clears storedTokens. Letting this redirect race the
            // bootstrap dumps the admin on family-account-selection (showing
            // the *previous* tenant's siblings) instead of the parent home.
            if (currentRoute?.name === "impersonate") {
                return
            }

            // Find current screen configuration (lazy require to avoid circular dependency)
            const {
                navigationScreens,
            } = require("@common/navigation/navigationScreens")
            const currentScreen = navigationScreens?.main?.find(
                (screen) => screen.name === currentRoute?.name
            )

            // Check if current screen has "auth" title
            const hasAuthTitle = currentScreen?.titles?.some(
                (title) =>
                    typeof title === "object" &&
                    Object.keys(title).includes("auth")
            )

            // If there are family accounts, redirect to family account selection first
            // Only redirect if we're on the menu screen with auth title
            if (
                !props.isParentMode &&
                !props.isLiveMode &&
                (currentRoute?.name === "menu" || !hasAuthTitle)
            ) {
                // Skip redirect when the auto-bypass hook will handle login —
                // avoids a flash of family-account-selection before the bypass
                // fires. resolveAutoBypassTarget is pure, safe to call twice.
                if (
                    props.pendingDeepLink &&
                    resolveAutoBypassTarget(
                        props.pendingDeepLink,
                        storedTokens,
                        props.familyState
                    )
                ) {
                    return
                }

                nav.replace("family-account-selection")
            }
        }
    }, [props.pendingDeepLink, storedTokens])

    // A blank screen while redirecting
    return (
        <View style={styles.container}>
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        storedTokens: state.data.authTokens?.storedTokens || {},
        familyState: state.data.family,
        isParentMode: state.data.family.isParentMode,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        pendingDeepLink: state.notifications.pendingDeepLink,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    switchAccount,
    setIsParentMode,
    setParentModeFamilyId,
    clearPendingDeepLink,
}

export default connect(mapStateToProps, mapDispatchToProps)(AuthNavigator)
